import { useState } from 'react';
import { RECOVERY_KEY, requiresAppLock } from '../data/mock';
import { usePerson } from '../data/people';
import { Icon } from '../components/Icon';
import {
  Button,
  ListRow,
  NavBar,
  OrgLogo,
  PersonAvatar,
  Section,
  Segmented,
  Toggle,
  cx,
} from '../components/ui';
import {
  openDetail,
  pop,
  setOnboarding,
  push,
  setState,
  signOut,
  toast,
  useStore,
  type SettingsPage,
} from '../state/store';
import { useActiveTheme } from '../state/theme';
import { useWide } from '../shell/adaptive';

export function YouScreen() {
  const me = usePerson('me');
  const theme = useActiveTheme();
  const wide = useWide();
  const email = useStore((s) => s.auth.email);
  const recoverySaved = useStore((s) => s.recoverySaved);
  const orgId = useStore((s) => s.orgId);
  const appLockOff = useStore((s) => s.onboarding.unlock === null) && !requiresAppLock(orgId);
  const selected = useStore((s) => {
    const top = s.stacks.you[s.stacks.you.length - 1];
    return top?.name === 'settings' ? top.page : undefined;
  });
  const go = (page: SettingsPage) =>
    wide ? openDetail({ name: 'settings', page }, 'you') : push({ name: 'settings', page }, 'you');

  return (
    <div className="screen grouped">
      <NavBar large title="You" />
      <div className="screen-scroll">
        <button type="button" className="profile-card" onClick={() => go('profile')}>
          <PersonAvatar id="me" size={64} />
          <span className="list-row-text">
            <span className="profile-name">{me.name}</span>
            <span className="list-row-subtitle">{me.title ?? email}</span>
            {theme.orgName ? (
              <span className="org-pill">
                <OrgLogo size={16} /> {theme.orgName}
              </span>
            ) : (
              <span className="org-pill neutral">No organisation</span>
            )}
          </span>
          <Icon name="chevronRight" size={18} className="list-row-chevron" />
        </button>

        {appLockOff && (
          <button type="button" className="notice-card" onClick={() => go('privacy')}>
            <span className="notice-icon">
              <Icon name="lock" size={20} />
            </span>
            <span className="list-row-text">
              <span className="list-row-title">Turn on app lock</span>
              <span className="list-row-subtitle">
                Ask for Face ID when Circles opens, so nobody else can read your chats.
              </span>
            </span>
            <Icon name="chevronRight" size={18} className="list-row-chevron" />
          </button>
        )}

        {!recoverySaved && (
          <button type="button" className="notice-card" onClick={() => go('recovery')}>
            <span className="notice-icon">
              <Icon name="key" size={20} />
            </span>
            <span className="list-row-text">
              <span className="list-row-title">Save your recovery key</span>
              <span className="list-row-subtitle">
                You need it to read your chat history on a new phone.
              </span>
            </span>
            <Icon name="chevronRight" size={18} className="list-row-chevron" />
          </button>
        )}

        <Section>
          <ListRow
            icon="key"
            title="Recovery key"
            chevron
            onClick={() => go('recovery')}
            selected={selected === 'recovery'}
            trailing={
              recoverySaved ? (
                <span className="row-value ok">Saved</span>
              ) : (
                <span className="row-dot" />
              )
            }
          />
          <ListRow
            icon="shield"
            title="Privacy"
            chevron
            onClick={() => go('privacy')}
            selected={selected === 'privacy'}
          />
          <ListRow
            icon="bell"
            title="Notifications"
            chevron
            onClick={() => go('notifications')}
            selected={selected === 'notifications'}
          />
          <ListRow
            icon="devices"
            title="Linked devices"
            chevron
            onClick={() => go('devices')}
            selected={selected === 'devices'}
            trailing={<span className="row-value">1 of 4</span>}
          />
        </Section>
        <Section>
          <ListRow
            icon="chats"
            title="Chats and storage"
            chevron
            onClick={() => go('chats')}
            selected={selected === 'chats'}
          />
          <ListRow
            icon="appearance"
            title="Appearance"
            chevron
            onClick={() => go('appearance')}
            selected={selected === 'appearance'}
          />
          <ListRow
            icon="help"
            title="Help"
            chevron
            onClick={() => go('help')}
            selected={selected === 'help'}
          />
        </Section>
        <Section>
          <ListRow icon="logout" title="Log out" danger onClick={signOut} />
        </Section>
        <p className="version-note">Circles 1.0 (prototype) · Hosted in Germany</p>
      </div>
    </div>
  );
}

export function SettingsScreen({ page }: { page: SettingsPage }) {
  const wide = useWide();
  const titles: Record<SettingsPage, string> = {
    profile: 'Profile',
    privacy: 'Privacy',
    notifications: 'Notifications',
    devices: 'Linked devices',
    appearance: 'Appearance',
    recovery: 'Recovery key',
    chats: 'Chats and storage',
    help: 'Help',
  };
  return (
    <div className="screen grouped">
      <NavBar title={titles[page]} onBack={wide ? undefined : pop} center={!wide} />
      <div className="screen-scroll">
        {page === 'profile' && <Profile />}
        {page === 'privacy' && <Privacy />}
        {page === 'notifications' && <Notifications />}
        {page === 'devices' && <Devices />}
        {page === 'appearance' && <Appearance />}
        {page === 'recovery' && <Recovery />}
        {page === 'chats' && <ChatsStorage />}
        {page === 'help' && <Help />}
      </div>
    </div>
  );
}

function Profile() {
  const me = usePerson('me');
  const theme = useActiveTheme();
  const email = useStore((s) => s.auth.email);
  return (
    <>
      <div className="profile-head">
        <PersonAvatar id="me" size={96} />
        <button type="button" className="text-link">
          Change photo
        </button>
      </div>
      <Section>
        <ListRow title="Name" trailing={<span className="row-value">{me.name}</span>} chevron />
        <ListRow
          title="Job title"
          trailing={<span className="row-value">{me.title ?? 'Add'}</span>}
          chevron
        />
        <ListRow
          title="Email"
          trailing={<span className="row-value">{email || 'anna.berg@nordwind-logistik.de'}</span>}
        />
        <ListRow title="Phone" trailing={<span className="row-value">+49 ••• •••• 214</span>} />
      </Section>
      <Section
        footer={
          theme.orgName
            ? 'Your organisation manages your membership. People outside it see this name next to yours.'
            : 'You aren’t part of an organisation. You can join chats you’re invited to.'
        }
      >
        <ListRow
          title="Organisation"
          trailing={<span className="row-value">{theme.orgName ?? 'None'}</span>}
        />
      </Section>
      <Section>
        <ListRow title="Export my data" chevron />
        <ListRow title="Delete account" danger />
      </Section>
    </>
  );
}

function Privacy() {
  const privacy = useStore((s) => s.privacy);
  const unlock = useStore((s) => s.onboarding.unlock);
  const required = requiresAppLock(useStore((s) => s.orgId));
  const theme = useActiveTheme();
  const set = (k: keyof typeof privacy) => (v: boolean) =>
    setState((s) => ({ privacy: { ...s.privacy, [k]: v } }));
  return (
    <>
      <Section
        title="Who can see"
        footer="If you turn off read receipts, you won’t see other people’s either."
      >
        <ListRow
          title="Read receipts"
          trailing={
            <Toggle
              checked={privacy.readReceipts}
              onChange={set('readReceipts')}
              label="Read receipts"
            />
          }
        />
        <ListRow
          title="Typing indicator"
          trailing={
            <Toggle checked={privacy.typing} onChange={set('typing')} label="Typing indicator" />
          }
        />
        <ListRow
          title="Last seen"
          trailing={
            <Toggle checked={privacy.lastSeen} onChange={set('lastSeen')} label="Last seen" />
          }
        />
      </Section>
      <Section title="Chats">
        <ListRow
          title="Chat requests"
          trailing={<span className="row-value">From anyone</span>}
          chevron
        />
        <ListRow
          title="Blocked people"
          trailing={<span className="row-value">None</span>}
          chevron
        />
      </Section>
      <Section
        title="Security"
        footer={
          required
            ? `${theme.orgName ?? 'Your organisation'} requires an app lock, so it can’t be turned off.`
            : 'Ask for Face ID or your device passcode when Circles opens.'
        }
      >
        <ListRow
          title="App lock"
          subtitle={
            unlock === 'passcode' ? 'Device passcode' : unlock === 'biometric' ? 'Face ID' : 'Off'
          }
          trailing={
            <Toggle
              checked={unlock !== null}
              disabled={required}
              onChange={(v) => setOnboarding({ unlock: v ? 'biometric' : null })}
              label="App lock"
            />
          }
        />
      </Section>
    </>
  );
}

function Notifications() {
  const [msg, setMsg] = useState(true);
  const [groups, setGroups] = useState(true);
  const [feed, setFeed] = useState(false);
  const [preview, setPreview] = useState(true);
  return (
    <>
      <Section title="Notify me about">
        <ListRow
          title="Messages"
          trailing={<Toggle checked={msg} onChange={setMsg} label="Messages" />}
        />
        <ListRow
          title="Groups"
          trailing={<Toggle checked={groups} onChange={setGroups} label="Groups" />}
        />
        <ListRow
          title="Feed: posts from official channels"
          trailing={<Toggle checked={feed} onChange={setFeed} label="Feed" />}
        />
        <ListRow
          title="Calls"
          trailing={<span className="row-value">Ring like a phone call</span>}
        />
      </Section>
      <Section footer="Previews are decrypted on this device. The notification server only ever sees an event ID.">
        <ListRow
          title="Show message preview"
          trailing={<Toggle checked={preview} onChange={setPreview} label="Show preview" />}
        />
      </Section>
    </>
  );
}

function Devices() {
  const [linking, setLinking] = useState(false);
  return (
    <>
      <div className="devices-hero">
        <Icon name="devices" size={44} />
        <p>
          Use Circles on up to 4 computers. Your phone verifies each one and shares your chat keys
          with it.
        </p>
        <Button icon="add" onClick={() => setLinking(true)}>
          Link a desktop
        </Button>
      </div>
      {linking && (
        <Section footer="Open Circles on your Mac or Windows PC and point your phone at the QR code it shows.">
          <div className="qr-scanner">
            <span className="qr-frame" />
            <span className="qr-hint">Scanning…</span>
          </div>
        </Section>
      )}
      <Section title="This phone">
        <ListRow icon="devices" title="iPhone" subtitle="Hamburg · active now" />
      </Section>
      <Section title="Linked desktops (1 of 4)">
        <ListRow
          icon="devices"
          title="MacBook Pro"
          subtitle="Linked 28 Oct · last active today 08:51"
          trailing={
            <button type="button" className="text-link danger">
              Unlink
            </button>
          }
        />
      </Section>
    </>
  );
}

function Appearance() {
  const mode = useStore((s) => s.mode);
  return (
    <>
      <Section
        title="Theme"
        footer="Your organisation sets the accent colour and logo. Light and dark work with every organisation design."
      >
        <div className="appearance-row">
          <Segmented
            value={mode}
            onChange={(v) => setState({ mode: v })}
            options={[
              { value: 'light', label: 'Light' },
              { value: 'dark', label: 'Dark' },
            ]}
          />
        </div>
      </Section>
      <Section title="Text size" footer="Circles follows the text size in your system settings.">
        <ListRow
          title="Use system text size"
          trailing={<Toggle checked onChange={() => undefined} label="Use system text size" />}
        />
      </Section>
    </>
  );
}

function Recovery() {
  const [revealed, setRevealed] = useState(false);
  const saved = useStore((s) => s.recoverySaved);
  const groups = RECOVERY_KEY.split(' ');
  return (
    <>
      <div className="recovery-hero">
        <span className="recovery-icon">
          <Icon name="key" size={30} />
        </span>
        <h2>Your recovery key</h2>
        <p>
          Your chats are end-to-end encrypted. On a new phone, this key unlocks your message
          history. Without it you keep your account and contacts, but old messages can’t be read —
          not even by Circles or your organisation.
        </p>
      </div>
      <div
        className={cx('recovery-key', !revealed && 'is-hidden')}
        aria-label={revealed ? 'Recovery key' : 'Recovery key, hidden'}
      >
        {groups.map((g, i) => (
          <code key={i}>{revealed ? g : '••••'}</code>
        ))}
        {!revealed && (
          <button type="button" className="recovery-reveal" onClick={() => setRevealed(true)}>
            <Icon name="lock" size={16} /> Show key
          </button>
        )}
      </div>
      <div className="recovery-actions">
        <Button
          variant="secondary"
          icon="copy"
          onClick={() => toast('Copied. Paste it into your password manager.')}
          disabled={!revealed}
        >
          Copy
        </Button>
        <Button variant="secondary" icon="share" disabled={!revealed}>
          Save to file
        </Button>
      </div>
      <Section footer="Keep it somewhere safe outside Circles, such as a password manager. Anyone with this key and your account can read your chats.">
        <ListRow
          title="I’ve saved my recovery key"
          trailing={
            <Toggle
              checked={saved}
              onChange={(v) => setState({ recoverySaved: v })}
              label="I’ve saved my recovery key"
            />
          }
        />
      </Section>
      <Section>
        <ListRow title="Create a new key" subtitle="Your current key stops working" danger />
      </Section>
    </>
  );
}

function ChatsStorage() {
  const [wifiOnly, setWifiOnly] = useState(true);
  return (
    <Section
      title="Automatic download"
      footer="On mobile data, photos still download; videos and documents wait for Wi-Fi."
    >
      <ListRow
        title="Videos and documents on Wi-Fi only"
        trailing={<Toggle checked={wifiOnly} onChange={setWifiOnly} label="Wi-Fi only" />}
      />
      <ListRow title="Storage used" trailing={<span className="row-value">412 MB</span>} chevron />
    </Section>
  );
}

function Help() {
  return (
    <Section>
      <ListRow title="Help centre" chevron />
      <ListRow title="Contact support" chevron />
      <ListRow title="Terms of use" chevron />
      <ListRow title="Privacy policy" chevron />
    </Section>
  );
}
